import React, { useState } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerFooter,
  Badge,
} from "@chakra-ui/react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { FiX } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel/FormikInputWithLabel";
import { useAdminUpdateUserMutation } from "./hooks/useAdminUpdateUserMutation";
import type { AdminUpdateUserPayload } from "./hooks/useAdminUpdateUserMutation";
import { useAdminResetUser2faMutation } from "./hooks/useAdminResetUser2faMutation";
import type { UserListItem } from "../../types/userInvite";
import { useCompany2faSettings } from "newui/features/Settings/hooks/useCompany2faSettings";
import { Modal } from "newui/components/ui/Modal";
import { useHasTwoFactorAuth } from "hooks/featureFlags";

interface EditUserDrawerProps {
  isOpen: boolean;
  user: UserListItem | null;
  onClose: () => void;
}

const validationSchema = Yup.object({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  phone: Yup.string().nullable(),
});

export const EditUserDrawer: React.FC<EditUserDrawerProps> = ({
  user,
  isOpen,
  onClose,
}) => {
  const update = useAdminUpdateUserMutation();
  const resetTwoFactor = useAdminResetUser2faMutation();
  const { data: company2FA, isLoading: is2FaLoading } = useCompany2faSettings();
  const is2faEnabled = useHasTwoFactorAuth();
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  // The `user` prop is a snapshot from the parent list, so a successful reset
  // isn't reflected until the list refetches and the drawer is re-opened.
  // Track the id we just reset to flip the local display immediately.
  const [resetUserId, setResetUserId] = useState<number | null>(null);

  if (!user) {
    return null;
  }

  const twoFactorEnabled = !!user.two_factor_enabled && resetUserId !== user.id;

  const handleConfirmReset = () => {
    resetTwoFactor.mutate(user.id, {
      onSuccess: () => {
        setResetUserId(user.id);
        setIsResetConfirmOpen(false);
      },
    });
  };

  const initialValues: AdminUpdateUserPayload = {
    first_name: user.first_name,
    last_name: user.last_name,
    phone: user.phone ?? "",
  };

  const handleSubmit = (values: AdminUpdateUserPayload) => {
    update.mutate(
      {
        userId: user.id,
        payload: {
          first_name: values.first_name,
          last_name: values.last_name,
          phone: values.phone || null,
        },
      },
      {
        onSuccess: () => onClose(),
      },
    );
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader className="flex items-center justify-between border-b border-gray-200">
          <span className="text-lg font-semibold text-gray-900">Edit User</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:text-gray-600"
            data-testid="edit-user-drawer-close"
          >
            <FiX size={20} />
          </button>
        </DrawerHeader>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form className="flex flex-col h-full">
              <DrawerBody className="space-y-4 py-6">
                {/* Read-only fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email address
                  </label>
                  <input
                    type="email"
                    value={user.email}
                    disabled
                    className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
                    data-testid="edit-user-email-readonly"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <input
                    type="text"
                    value={user.role?.display_name ?? user.role?.name ?? "—"}
                    disabled
                    className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
                    data-testid="edit-user-role-readonly"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <input
                    type="text"
                    value={user.status}
                    disabled
                    className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 cursor-not-allowed capitalize"
                    data-testid="edit-user-status-readonly"
                  />
                </div>

                {/* Editable fields */}
                <FormikInputWithLabel
                  name="first_name"
                  label="First name"
                  placeholder="First name"
                  required
                  testId="edit-user-first-name"
                />
                <FormikInputWithLabel
                  name="last_name"
                  label="Last name"
                  placeholder="Last name"
                  required
                  testId="edit-user-last-name"
                />
                <FormikInputWithLabel
                  name="phone"
                  label="Phone number"
                  placeholder="+61 4XX XXX XXX"
                  type="tel"
                  testId="edit-user-phone"
                />
                {!is2FaLoading && is2faEnabled &&
                  <div className="mt-10">
                    <Badge className="mb-2" background={twoFactorEnabled ? "green.500" : "red.400"} color="white">
                      {!twoFactorEnabled && "Not "} Enrolled in 2FA
                    </Badge>
                    {twoFactorEnabled && <Button type="button" btntype="secondary" onClick={() => setIsResetConfirmOpen(true)} data-testid="reset-2fa-btn">Reset User 2FA</Button>}
                  </div>}
              </DrawerBody>

              <DrawerFooter className="border-t border-gray-200">
                <div className="flex w-full justify-end gap-3">
                  <Button
                    type="button"
                    onClick={onClose}
                    data-testid="edit-user-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={update.isPending || isSubmitting}
                    data-testid="edit-user-save"
                  >
                    {update.isPending ? "Saving…" : "Save changes"}
                  </Button>
                </div>
              </DrawerFooter>
            </Form>
          )}
        </Formik>
      </DrawerContent>
      <Modal
        title="Reset User Two Factor Authentication?"
        modalType="alert"
        iconType="warning"
        isOpen={isResetConfirmOpen}
        onClose={() => setIsResetConfirmOpen(false)}
        closeOnEsc
        closeOnOverlayClick
        primaryButton={{
          text: "Reset Two Factor Authentication",
          btntype: "danger",
          onClick: handleConfirmReset,
          isLoading: resetTwoFactor.isPending,
          disabled: resetTwoFactor.isPending,
        }}
        secondaryButton={{
          text: "Cancel",
          btntype: "secondary",
          onClick: () => setIsResetConfirmOpen(false),
          disabled: resetTwoFactor.isPending,
        }}
      >
        <div className="text-center text-md space-y-4 mb-4 text-gray-600">
          <p>
            Are you sure you want to reset two-factor authentication for{" "}
            <strong>
              {user.first_name} {user.last_name}
            </strong>
            ?{" "}
          </p>
          <p>
            {company2FA?.is_2fa_enforced
              ? "They will be required to set up 2FA again on their next login."
              : "This will disable 2FA for this user and they will have to set it up again."}
          </p>
        </div>
      </Modal>
    </Drawer >
  );
};
